import React, { useState } from 'react';
import styles from './FlightBookingWidget.module.scss';
import type { FlightCardPayload } from './FlightCardWidget';
import { useProfile } from '../context/ProfileContext';

// Flights have no per-offer photo (unlike hotel rooms) — same illustrative
// Unsplash source FlightDetailsStep's fare tiers already use, so a flight's
// Trips-list thumbnail isn't just a bare icon.
const FLIGHT_BOOKING_IMAGE = 'https://images.unsplash.com/photo-1540339832862-474599807836?w=160&h=160&fit=crop&q=80';

// ── Inline SVG Icons ─────────────────────────────────────────

function PlaneIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
    );
}

function ShieldIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    );
}

function CheckIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5L20 7" />
        </svg>
    );
}

function DocumentIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    );
}

function ScanIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
    );
}

function PersonIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </svg>
    );
}

function ChevronDownIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}

function CalendarIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    );
}

function GlobeIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
    );
}

function CreditCardIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
    );
}

// ── Types ────────────────────────────────────────────────────

export type PassengerInfo = {
    lastName: string;
    firstName: string;
    dateOfBirth: string;
    gender: 'female' | 'male' | '';
    citizenship: string;
    documentType: string;
    documentNumber: string;
    validUntil: string;
};

export type PaymentInfo = {
    cardNumber: string;
    expiry: string;
    cvc: string;
};

export type FlightBookingResult = {
    agreed: boolean;
    passenger: PassengerInfo;
    payment: PaymentInfo;
};

type FlightBookingWidgetProps = {
    booking: FlightCardPayload;
    onContinue: (result: FlightBookingResult) => void;
};

// ── Component ────────────────────────────────────────────────

export function FlightBookingWidget({ booking, onContinue }: FlightBookingWidgetProps) {
    const { addBooking } = useProfile();
    const [agreed, setAgreed] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [passenger, setPassenger] = useState<PassengerInfo>({
        lastName: '',
        firstName: '',
        dateOfBirth: '',
        gender: '',
        citizenship: 'Russia',
        documentType: 'International passport (RF)',
        documentNumber: '',
        validUntil: '',
    });
    const [payment, setPayment] = useState<PaymentInfo>({
        cardNumber: '',
        expiry: '',
        cvc: '',
    });

    const basePrice = parseFloat(booking.price.total) || 0;
    const currency = booking.price.currency || '$';
    const displayPrice = `${currency === 'USD' ? '$' : currency}${basePrice.toFixed(0)}`;

    const updatePassenger = (field: keyof PassengerInfo, value: string) => {
        setPassenger((prev) => ({ ...prev, [field]: value }));
    };

    const updatePayment = (field: keyof PaymentInfo, value: string) => {
        setPayment((prev) => ({ ...prev, [field]: value }));
    };

    /**
     * Same simulated checkout as HotelBookingWidget's handlePayment: a
     * timed delay standing in for a real charge, then a Booking banked
     * into the profile directly (this widget has no AI/gateway turn of
     * its own to derive one from — see Chat.tsx's real order_confirmation
     * path for that).
     */
    const handleContinue = async () => {
        setProcessing(true);
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
        setProcessing(false);

        const itineraries = booking.itineraries || [];
        const firstSegments = itineraries[0]?.segments || [];
        const firstSegment = firstSegments[0];
        const lastItinerary = itineraries[itineraries.length - 1] || itineraries[0];
        const lastSegments = lastItinerary?.segments || [];
        const lastSegment = lastSegments[lastSegments.length - 1] || firstSegment;

        const origin = firstSegment?.departure?.iata || '';
        const destination = lastSegment?.arrival?.iata || '';
        const travellerName = [passenger.firstName, passenger.lastName].filter(Boolean).join(' ');
        const airline = booking.carriers?.[0] || booking.validating_airline || '';

        const startDate = firstSegment?.departure?.at
            ? new Date(firstSegment.departure.at).toISOString()
            : new Date().toISOString();
        const endDate = lastSegment?.arrival?.at
            ? new Date(lastSegment.arrival.at).toISOString()
            : startDate;

        addBooking({
            kind: 'flight',
            title: origin && destination ? `${origin} → ${destination}` : airline || 'Flight',
            subtitle: travellerName || undefined,
            startDate,
            endDate,
            amount: basePrice,
            currency: booking.price.currency || 'USD',
            status: 'confirmed',
            reference: `FL-${booking.offer_id}`,
            destination: destination || undefined,
            image: FLIGHT_BOOKING_IMAGE,
        });

        onContinue({ agreed, passenger, payment });
    };

    return (
        <section className={styles.root}>
            {/* Header */}
            <div className={styles.headerRow}>
                <div className={styles.headerLeft}>
                    <PlaneIcon className={styles.headerIcon} />
                    <span className={styles.headerTitle}>Flight payment</span>
                    <span className={styles.headerStep}>Step 2 of 5</span>
                </div>
                <div className={styles.headerRight}>
                    <ShieldIcon className={styles.shieldIcon} />
                    <span>Secure booking</span>
                </div>
            </div>

            {/* 1. Agreement checkbox */}
            <div className={styles.agreementRow}>
                <div
                    className={`${styles.checkbox} ${agreed ? styles.checked : ''}`}
                    onClick={() => setAgreed(!agreed)}
                    role="checkbox"
                    aria-checked={agreed}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAgreed(!agreed); }}
                >
                    {agreed && <CheckIcon className={styles.checkIcon} />}
                </div>
                <span className={styles.agreementText}>
                    I agree to the{' '}
                    <a href="#fare-rules" className={styles.agreementLink} onClick={(e) => e.preventDefault()}>
                        fare rules, refund and exchange conditions
                    </a>
                </span>
            </div>

            {/* 2. Passenger information form */}
            <div className={styles.passengerCard}>
                {/* Info banner */}
                <div className={styles.infoBanner}>
                    <div className={styles.infoBannerLeft}>
                        <DocumentIcon className={styles.docIcon} />
                        <div className={styles.infoBannerText}>
                            <span className={styles.infoBannerTitle}>Fill in as in your document</span>
                            <span className={styles.infoBannerSub}>
                                To avoid issues during check-in, your details must match your passport or ID.
                            </span>
                        </div>
                    </div>
                    <a href="#learn-more" className={styles.learnMoreLink} onClick={(e) => e.preventDefault()}>
                        Learn more
                    </a>
                </div>

                <div className={styles.passengerBody}>
                    <h3 className={styles.passengerHeading}>Passenger 1 (12 years and older)</h3>

                    {/* Action buttons */}
                    <div className={styles.actionButtons}>
                        <button type="button" className={styles.actionBtn}>
                            <ScanIcon className={styles.actionBtnIcon} />
                            Scan document
                        </button>
                        <button type="button" className={styles.actionBtn}>
                            <PersonIcon className={styles.actionBtnIcon} />
                            Choose passenger
                            <ChevronDownIcon className={styles.actionBtnIcon} />
                        </button>
                    </div>

                    {/* Row 1: Last name, First name */}
                    <div className={`${styles.formRow} ${styles.formRow2}`}>
                        <div className={styles.inputGroup}>
                            <input
                                type="text"
                                className={styles.inputField}
                                placeholder="Last name"
                                value={passenger.lastName}
                                onChange={(e) => updatePassenger('lastName', e.target.value)}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <input
                                type="text"
                                className={styles.inputField}
                                placeholder="First name"
                                value={passenger.firstName}
                                onChange={(e) => updatePassenger('firstName', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Row 2: Date of birth, Gender buttons */}
                    <div className={`${styles.formRow} ${styles.formRowGender}`}>
                        <div className={`${styles.inputGroup} ${styles.inputWithIcon}`}>
                            <input
                                type="text"
                                className={styles.inputField}
                                placeholder="Date of birth"
                                value={passenger.dateOfBirth}
                                onChange={(e) => updatePassenger('dateOfBirth', e.target.value)}
                            />
                            <CalendarIcon className={styles.inputIcon} />
                        </div>
                        <div className={styles.genderBtnGroup}>
                            <button
                                type="button"
                                className={`${styles.genderBtn} ${passenger.gender === 'female' ? styles.active : ''}`}
                                onClick={() => updatePassenger('gender', 'female')}
                            >
                                Female
                            </button>
                            <button
                                type="button"
                                className={`${styles.genderBtn} ${passenger.gender === 'male' ? styles.active : ''}`}
                                onClick={() => updatePassenger('gender', 'male')}
                            >
                                Male
                            </button>
                        </div>
                    </div>

                    {/* Citizenship dropdown */}
                    <div className={styles.formRow}>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel}>Citizenship</span>
                            <div className={styles.selectWrapper}>
                                <select
                                    className={styles.selectField}
                                    value={passenger.citizenship}
                                    onChange={(e) => updatePassenger('citizenship', e.target.value)}
                                >
                                    <option value="Russia">Russia</option>
                                    <option value="United States">United States</option>
                                    <option value="United Kingdom">United Kingdom</option>
                                    <option value="Germany">Germany</option>
                                    <option value="France">France</option>
                                    <option value="Other">Other</option>
                                </select>
                                <ChevronDownIcon className={styles.selectArrow} />
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Document type, Passport number, Valid until */}
                    <div className={`${styles.formRow} ${styles.formRow3}`}>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel}>Document type</span>
                            <div className={styles.selectWrapper}>
                                <select
                                    className={styles.selectField}
                                    value={passenger.documentType}
                                    onChange={(e) => updatePassenger('documentType', e.target.value)}
                                >
                                    <option value="International passport (RF)">International passport (RF)</option>
                                    <option value="National passport">National passport</option>
                                    <option value="ID card">ID card</option>
                                    <option value="Birth certificate">Birth certificate</option>
                                </select>
                                <ChevronDownIcon className={styles.selectArrow} />
                            </div>
                        </div>
                        <div className={styles.inputGroup}>
                            <span className={styles.inputLabel}>&nbsp;</span>
                            <input
                                type="text"
                                className={styles.inputField}
                                placeholder="Document number"
                                value={passenger.documentNumber}
                                onChange={(e) => updatePassenger('documentNumber', e.target.value)}
                            />
                        </div>
                        <div className={`${styles.inputGroup} ${styles.inputWithIcon}`}>
                            <span className={styles.inputLabel}>&nbsp;</span>
                            <input
                                type="text"
                                className={styles.inputField}
                                placeholder="Valid until"
                                value={passenger.validUntil}
                                onChange={(e) => updatePassenger('validUntil', e.target.value)}
                            />
                            <CalendarIcon className={styles.inputIcon} />
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. Entry requirements */}
            <h2 className={styles.sectionTitle}>Entry requirements</h2>
            <div className={styles.entryCard}>
                <div className={styles.entryCardLeft}>
                    <GlobeIcon className={styles.globeIcon} />
                    <div className={styles.entryCardText}>
                        <span className={styles.entryCardTitle}>United States of America</span>
                        <span className={styles.entryCardSub}>Check the current entry requirements before your trip.</span>
                    </div>
                </div>
                <a href="#learn-more-entry" className={styles.learnMoreLink} onClick={(e) => e.preventDefault()}>
                    Learn more
                </a>
            </div>

            {/* 4. Payment section */}
            <h2 className={styles.sectionTitle}>Payment</h2>
            <div className={styles.paymentCard}>
                <div className={styles.paymentHeader}>
                    <div className={styles.paymentHeaderLeft}>
                        <span className={styles.paymentTitle}>Pay with card</span>
                        <span className={styles.paymentSub}>
                            We accept Visa, Mastercard, American Express and other cards.
                        </span>
                    </div>
                    <div className={styles.paymentLogos}>
                        <span className={styles.paymentLogo}>VISA</span>
                        <span className={styles.paymentLogo}>MC</span>
                        <span className={styles.paymentLogo}>AMEX</span>
                        <span className={styles.paymentLogo}>Pay</span>
                    </div>
                </div>

                <div className={styles.paymentFields}>
                    <div className={styles.cardNumberField}>
                        <CreditCardIcon className={styles.cardFieldIcon} />
                        <input
                            type="text"
                            className={styles.inputField}
                            placeholder="Card number"
                            value={payment.cardNumber}
                            onChange={(e) => updatePayment('cardNumber', e.target.value)}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="text"
                            className={styles.inputField}
                            placeholder="MM / YY"
                            value={payment.expiry}
                            onChange={(e) => updatePayment('expiry', e.target.value)}
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="text"
                            className={styles.inputField}
                            placeholder="CVC"
                            value={payment.cvc}
                            onChange={(e) => updatePayment('cvc', e.target.value)}
                        />
                    </div>
                    <div className={styles.helpIcon} title="What is CVC?">?</div>
                </div>
            </div>

            {/* 5. Sticky checkout bar */}
            <div className={styles.checkoutBar}>
                <div className={styles.checkoutLeft}>
                    <div className={styles.checkoutLabel}>
                        <span className={styles.checkoutTitle}>Total</span>
                        <span className={styles.checkoutPassengers}>1 passenger</span>
                    </div>
                    <div className={styles.checkoutPrice}>
                        <span className={styles.checkoutAmount}>{displayPrice}</span>
                        <ChevronDownIcon className={styles.checkoutArrow} />
                    </div>
                </div>
                <button type="button" className={styles.continueBtn} onClick={handleContinue} disabled={processing}>
                    {processing ? 'Processing...' : 'Continue to payment'}
                </button>
            </div>
        </section>
    );
}
